import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { Dispatch, ReactNode, SetStateAction } from 'react'
import { Area, AreaChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts'
import { RowBetween } from '../Row'
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import { Loading } from '@/components/ui/loading'
import { cn } from '@/lib/utils'

dayjs.extend(utc)

const DEFAULT_HEIGHT = 300

export type LineChartProps = {
  data: any[]
  color?: string | undefined
  height?: number | undefined
  minHeight?: number
  setValue?: Dispatch<SetStateAction<number | undefined>> // used for value on hover
  setLabel?: Dispatch<SetStateAction<string | undefined>> // used for label of value
  value?: number
  label?: string
  topLeft?: ReactNode | undefined
  topRight?: ReactNode | undefined
  bottomLeft?: ReactNode | undefined
  bottomRight?: ReactNode | undefined
} & React.HTMLAttributes<HTMLDivElement>

const chartConfig = {
  desktop: {
    label: 'Desktop',
    color: 'var(--zap)',
  },
} satisfies ChartConfig

const Chart = ({
  data,
  color = '#1FC7D4',
  value,
  label,
  setValue,
  setLabel,
  topLeft,
  topRight,
  bottomLeft,
  bottomRight,
  height,
  minHeight = DEFAULT_HEIGHT,
  ...rest
}: LineChartProps) => {
  const parsedValue = value

  return (
    <div
      {...rest}
      className={cn('flex flex-col', rest.className)}
      style={{
        minHeight: `${minHeight}px`,
      }}
    >
      <div className={'flex justify-between'}>
        {topLeft ?? null}
        {topRight ?? null}
      </div>
      {data?.length === 0 ? (
        <div className={'flex items-center justify-center grow'}>
          <Loading />
        </div>
      ) : (
        <div style={{ height }} className={'pb-6'}>
          <ChartContainer config={chartConfig} className={'w-full'} style={{ height, overflow: 'visible' }}>
            <AreaChart
              data={data}
              onMouseLeave={() => {
                if (setLabel) setLabel(undefined)
                if (setValue) setValue(undefined)
              }}
            >
              <defs>
                <linearGradient id="fillDesktop" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-desktop)" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="var(--color-desktop)" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="time"
                tickLine={false}
                axisLine={false}
                tickMargin={10}
                tickFormatter={(time) => dayjs(time).format('DD')}
              />
              <CartesianGrid vertical={false} horizontal={false} />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    className={'hidden'}
                    formatter={(tooltipValue, name, props) => {
                      if (setValue && parsedValue !== props.payload.value) {
                        setValue(props.payload.value)
                      }
                      const formattedTime = dayjs(props.payload.time).format('MMM D, YYYY')
                      if (setLabel && label !== formattedTime) setLabel(formattedTime)
                      return ''
                    }}
                  />
                }
              />
              <Area
                dataKey="value"
                type="bump"
                fill="url(#fillDesktop)"
                fillOpacity={0.4}
                stroke="var(--color-desktop)"
                strokeWidth={2}
                stackId="a"
                style={{ zIndex: 100000 }}
                height={160}
              />
            </AreaChart>
          </ChartContainer>
        </div>
      )}
      <RowBetween>
        {bottomLeft ?? null}
        {bottomRight ?? null}
      </RowBetween>
    </div>
  )
}

export default Chart
